import { useQuery } from "@tanstack/react-query";
import { RefreshCw, Save } from "lucide-react";
import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";

import { Btn, Chip, Field, Loading, Page, Section, Select, SuggestField } from "../components/wf";
import { api, emailResultMessage } from "../lib/api";
import { usePerms } from "../lib/auth";
import { useCustomers, useKastle, useRefreshJobs, useStatuses, useSuggestions } from "../lib/queries";
import { DraftBanner, SaveStatus, useAutoSave, useDraft } from "../lib/autosave";

const woBody = (form) => ({
  ...form,
  budget: form.billing_type === "budget" ? parseFloat(form.budget) || 0 : 0,
  hourly_rate: form.billing_type === "hourly" ? parseFloat(form.hourly_rate) || 0 : 0,
});

const empty = {
  customer_id: "", job_description: "", description_details: "", hours_availability: "", status: "", job_notes: "", drive_link: "",
  budget: "", billing_type: "", hourly_rate: "", job_photos: [], floor_plans_available: false, floor_plans: [], assigned_to: [], kastle_pm_id: "",
};

function fromWo(w) {
  return {
    customer_id: w.customer_id, job_description: w.job_description, description_details: w.description_details, hours_availability: w.hours_availability,
    status: w.status, job_notes: w.job_notes, drive_link: w.drive_link, budget: w.budget ? String(w.budget) : "", billing_type: w.billing_type || (w.budget ? "budget" : ""),
    hourly_rate: w.hourly_rate ? String(w.hourly_rate) : "", job_photos: w.job_photos ?? [], floor_plans_available: !!w.floor_plans_available || !!w.floor_plans?.length,
    floor_plans: w.floor_plans ?? [], assigned_to: w.assigned_to ?? [], kastle_pm_id: w.kastle_pm_id ?? "",
  };
}

export default function WorkOrderForm() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const refresh = useRefreshJobs();
  const { isManager } = usePerms();
  const [form, setForm] = useState(empty);
  const [loaded, setLoaded] = useState(!id);
  const [saving, setSaving] = useState(false);
  const [prefilled, setPrefilled] = useState(false);
  const [asProject, setAsProject] = useState(false);
  const existing = useQuery({ queryKey: ["work-order", id], queryFn: () => api(`/work-orders/${id}`), enabled: !!id });
  const customers = useCustomers();
  const statuses = useStatuses();
  const kastle = useKastle();
  const people = useQuery({ queryKey: ["users-assignable"], queryFn: () => api("/users-assignable"), enabled: isManager });
  const sJob = useSuggestions("job_description");
  const sHours = useSuggestions("hours_availability");
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (existing.data && !loaded) {
      setForm(fromWo(existing.data));
      setLoaded(true);
    }
  }, [existing.data, loaded]);
  useEffect(() => {
    if (!id && !form.status && statuses.data?.length) setForm((f) => ({ ...f, status: statuses.data[0].name }));
  }, [statuses.data, id, form.status]);

  const selectCustomer = async (cid) => {
    setForm((f) => ({ ...f, customer_id: cid }));
    if (id || !cid) return;
    try {
      const last = (await api(`/work-orders?customer_id=${cid}`))[0];
      if (last) {
        const p = fromWo(last);
        setForm((f) => ({ ...f, ...p, customer_id: cid, status: f.status, job_photos: [], floor_plans: [], floor_plans_available: false, assigned_to: f.assigned_to, hourly_rate: f.hourly_rate }));
      }
      setPrefilled(!!last);
    } catch {
      setPrefilled(false);
    }
  };
  useEffect(() => {
    const c = params.get("customer");
    if (!id && c) selectCustomer(c);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const customer = customers.data?.find((c) => c.id === form.customer_id);

  const save = async () => {
    if (!form.customer_id) return toast.error("Select or add a customer first");
    setSaving(true);
    try {
      const res = await api(id ? `/work-orders/${id}` : "/work-orders", { method: id ? "PUT" : "POST", body: woBody(form) });
      if (!id) draft.clear();
      refresh(res);
      if (res.email_result) (res.email_result.ok ? toast.success : toast.error)(emailResultMessage(res.email_result));
      toast.success(id ? "Work order updated" : "Work order created");
      if (!id && asProject) {
        const p = await api(`/work-orders/${res.id}/project`, { method: "POST" });
        nav(`/projects/${p.id}`, { replace: true });
      } else nav(`/work-orders/${res.id}`, { replace: true });
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const auto = useAutoSave(form, async (d) => {
    const res = await api(`/work-orders/${id}`, { method: "PUT", body: woBody(d) });
    refresh(res);
    if (res.email_result) (res.email_result.ok ? toast.success : toast.error)(emailResultMessage(res.email_result));
  }, { ready: !!id && loaded, enabled: !!form.customer_id });
  const draft = useDraft("draft:work-order:new", form, setForm, !id && !params.get("customer"));

  if (id && existing.isLoading) return <Loading />;

  return (
    <Page title={id ? "Edit Job" : "New Job"} back testid="work-order-form"
      right={id ? <><SaveStatus status={auto.status} /><Btn variant="secondary" onClick={() => nav(`/work-orders/${id}`)} data-testid="work-order-done-button">Done</Btn></>
        : <Btn icon={Save} loading={saving} onClick={save} data-testid="work-order-save-button">Create Job</Btn>}>
      <DraftBanner draft={draft} onDiscard={() => { draft.clear(); setForm({ ...empty, status: statuses.data?.[0]?.name ?? "" }); }} />
      <div className="max-w-2xl">
        <Select label="Customer *" testid="work-order-customer-select" value={form.customer_id} disabled={!!id} placeholder="Select a customer"
          options={(customers.data ?? []).map((c) => ({ value: c.id, label: [c.name, c.city].filter(Boolean).join(" · ") }))} onChange={selectCustomer} />
        {customer && <p className="-mt-2 mb-3 text-sm text-wf-mute">{[customer.address, customer.city, customer.state].filter(Boolean).join(", ")}</p>}
        {!id && <Link to="/customers/new?pick=1" data-testid="work-order-add-customer-button" className="mb-4 inline-block text-sm font-semibold text-wf-brand hover:underline">Customer not in the system? Add new customer</Link>}
        {prefilled && (
          <p data-testid="prefill-banner" className="mb-4 flex items-center gap-2 rounded-xl bg-wf-soft p-3 text-sm text-wf-softink">
            <RefreshCw size={16} /> Details carried over from this customer's last work order. Edit as needed.
          </p>
        )}
        {!id && isManager && (
          <>
            <p className="mb-2 text-[13px] font-semibold">Will this be a project?</p>
            <div className="mb-4 flex gap-2">
              <Chip label="No" selected={!asProject} onClick={() => setAsProject(false)} testid="wo-project-no" />
              <Chip label="Yes, make it a project" selected={asProject} onClick={() => setAsProject(true)} testid="wo-project-yes" />
            </div>
          </>
        )}
        <Section title="Job" />
        <SuggestField suggestions={sJob.data} label="Job Description" testid="wo-job-description-input" value={form.job_description} onChange={set("job_description")} placeholder="Access control install" />
        <Field label="Description Details" testid="wo-description-details-input" value={form.description_details} onChange={set("description_details")} multiline />
        <SuggestField suggestions={sHours.data} label="Hours of Availability" testid="wo-hours-availability-input" value={form.hours_availability} onChange={set("hours_availability")} placeholder="Mon-Fri 7am-3pm" />
        <Select label="Status" testid="wo-status-dropdown" value={form.status} options={(statuses.data ?? []).map((s) => s.name)} onChange={set("status")} />
        {isManager && (
          <>
            <p className="mb-2 text-[13px] font-semibold text-wf-ink2">Assigned To</p>
            <div className="mb-4 flex flex-wrap gap-2" data-testid="wo-assigned-to">
              {(people.data ?? []).map((u) => {
                const on = form.assigned_to.includes(u.id);
                return <Chip key={u.id} testid={`wo-assign-${u.id}`} label={`${u.name}${u.role === "admin" ? " (Admin)" : ""}`} selected={on}
                  onClick={() => setForm((f) => ({ ...f, assigned_to: on ? f.assigned_to.filter((x) => x !== u.id) : [...f.assigned_to, u.id] }))} />;
              })}
            </div>
          </>
        )}
        <Select label="Kastle Project Manager" testid="wo-kastle-pm-select" value={form.kastle_pm_id} placeholder="None"
          options={(kastle.data ?? []).map((k) => ({ value: k.id, label: [k.name, k.location].filter(Boolean).join(" · ") }))} onChange={set("kastle_pm_id")} />
        <Field label="Job Notes" testid="wo-job-notes-input" value={form.job_notes} onChange={set("job_notes")} multiline />
        {isManager && (
          <>
            <p className="mb-2 text-[13px] font-semibold">Is this job on a budget or hourly?</p>
            <div className="mb-4 flex gap-2">
              <Chip label="Budget" selected={form.billing_type === "budget"} onClick={() => setForm((f) => ({ ...f, billing_type: "budget" }))} testid="wo-billing-budget" />
              <Chip label="Hourly" selected={form.billing_type === "hourly"} testid="wo-billing-hourly"
                onClick={() => setForm((f) => ({ ...f, billing_type: "hourly", hourly_rate: f.hourly_rate || (customer?.billing_rate ? String(customer.billing_rate) : "") }))} />
            </div>
            {form.billing_type === "budget" && <Field label="Budget ($)" testid="wo-budget-input" type="number" step="0.01" value={form.budget} onChange={set("budget")} placeholder="0.00" />}
            {form.billing_type === "hourly" && <Field label="Billing Rate per Man Hour ($)" testid="wo-hourly-rate-input" type="number" step="0.01" value={form.hourly_rate} onChange={set("hourly_rate")} placeholder="0.00" />}
          </>
        )}
        <p className="mb-2 text-[13px] font-semibold">Are floor plans available?</p>
        <div className="mb-4 flex gap-2">
          <Chip label="No" selected={!form.floor_plans_available} onClick={() => setForm((f) => ({ ...f, floor_plans_available: false }))} testid="wo-floor-plans-no" />
          <Chip label="Yes" selected={form.floor_plans_available} onClick={() => setForm((f) => ({ ...f, floor_plans_available: true }))} testid="wo-floor-plans-yes" />
        </div>
        <Field label="Google Drive Folder Link (optional)" testid="wo-drive-link-input" type="url" value={form.drive_link} onChange={set("drive_link")} placeholder="https://drive.google.com/..." />
        <p className="text-sm text-wf-mute">Job photos and floor plans can be added from the job page after saving.</p>
      </div>
    </Page>
  );
}
